// ============================================================================
// use-quotemaker-written-notices.ts
// ----------------------------------------------------------------------------
// A builder anywhere in the app hears when QuoteMaker has written a quote, or
// a new version of one, for another job (Peter, 2026-11-01: a notice with
// "Review it", nothing they are doing interrupted). Review it opens the job,
// where the Assistant brings QuoteMaker's review forward
// (use-quotemaker-review.ts). The job on screen needs no notice: its own
// QuoteMaker bar loads the quote and the review opens there.
//   - Reads GET /api/brain/runs/written once a minute while the tab is
//     visible. The server answers with its own time, the next read's `since`,
//     so a browser clock that is fast or slow misses nothing and repeats
//     nothing. The first read only sets that time: a quote written before
//     this tab opened is reviewed when the job is opened.
//   - At most three notices, newest first. Each stays until it is used or
//     closed.
// ============================================================================
import { useCallback, useEffect, useRef, useState } from 'react';
import { listQuoteMakerWritten, type QuoteMakerWrittenRun } from '../services/quote-brain-runs';

export const WRITTEN_NOTICE_POLL_MS = 60_000;
const MAX_NOTICES = 3;

export function useQuoteMakerWrittenNotices({ enabled, currentProjectId }: { enabled: boolean; currentProjectId: string | null }) {
    const [notices, setNotices] = useState<QuoteMakerWrittenRun[]>([]);
    const cursorRef = useRef<string | null>(null);
    const currentRef = useRef(currentProjectId);
    currentRef.current = currentProjectId;

    useEffect(() => {
        if (!enabled) return undefined;
        let alive = true;
        const read = async () => {
            if (typeof document !== 'undefined' && document.hidden && cursorRef.current) return;
            try {
                const answer = await listQuoteMakerWritten(cursorRef.current);
                if (!alive) return;
                const first = cursorRef.current === null;
                cursorRef.current = answer.now;
                if (first) return;
                const fresh = answer.runs.filter((r) => r.projectId !== currentRef.current);
                if (!fresh.length) return;
                setNotices((held) => {
                    const known = new Set(held.map((n) => n.runId));
                    return [...fresh.filter((r) => !known.has(r.runId)), ...held].slice(0, MAX_NOTICES);
                });
            } catch {
                /* read again on the next minute */
            }
        };
        void read();
        const timer = setInterval(() => { void read(); }, WRITTEN_NOTICE_POLL_MS);
        return () => {
            alive = false;
            clearInterval(timer);
        };
    }, [enabled]);

    // A job opened by any other way needs no notice of its own.
    useEffect(() => {
        if (!currentProjectId) return;
        setNotices((held) => (held.some((n) => n.projectId === currentProjectId) ? held.filter((n) => n.projectId !== currentProjectId) : held));
    }, [currentProjectId]);

    const dismiss = useCallback((runId: string) => {
        setNotices((held) => held.filter((n) => n.runId !== runId));
    }, []);

    return { notices, dismiss };
}
